/**
 * Gesprächsmodus: zuhören → verstehen → KIAN fragen → vorlesen → wieder zuhören.
 *
 * Halbduplex: Solange KIAN spricht, ist das Mikrofon aus (kein Echo, der Lautsprecher bleibt am
 * Handy der Lautsprecher). Wer dazwischenreden will, tippt „Unterbrechen".
 *
 * Entwürfe: Hat KIAN einen einzelnen Entwurf vorbereitet, hängt der Server das Vorlesen an die
 * Antwort an (Kunde, Status, ob der Kunde eine Mail bekommt) und sagt, ob er per Sprache bestätigt
 * werden darf. Ausgeführt wird erst auf „Ja, anlegen" (Vorgabe Kevin). Ein nacktes „Ja" bekommt
 * einen Hinweis, alles andere geht als Nachricht an KIAN.
 *
 * Alles, was gesprochen wird, steht im Chat (Vorgabe Kevin): Fragen und Antworten sowieso, die
 * übrigen Züge („Ja, anlegen", „Nein", „Stopp", das Ergebnis) speichert `POST /kian/voice/turn` —
 * der Server baut die Antworttexte, vorgelesen wird, was dort gespeichert ist.
 *
 * Geht die Seite in den Hintergrund (Handy gesperrt, App gewechselt), endet das Gespräch: ein
 * offenes Mikrofon, von dem niemand weiß, darf es nicht geben.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { ApiError } from '../../../lib/api/http';
import type { ChatMessage } from '../../../lib/api/domains/assistant';
import { transcribeKianVoice, type KianVoiceTurnKind, type KianVoiceTurnResult } from '../../../lib/api/domains/kianVoice';
import { kianActionKey } from '../actions/useKianAction';
import { createAudioContext, isCaptureSupported, startCapture, type CaptureSession } from './audioCapture';
import { SpeechPlayer } from './speechPlayer';
import { toSpeakableText } from './speakable';
import { classifyConfirmation, normalizeUtterance } from './voiceIntent';

export type VoicePhase = 'off' | 'starting' | 'listening' | 'transcribing' | 'thinking' | 'speaking' | 'executing';

export interface VoiceReply {
  id: string;
  content: string;
  streaming: boolean;
  /** Vom Server angehängtes Vorlesen eines Entwurfs (Text steht schon in `content`) */
  voiceDraft: { actionId: string; verb: 'anlegen' | 'ausführen'; confirmable: boolean } | null;
}

export interface VoiceUsage {
  heard: number;
  sttCostEur: number;
  spokenChars: number;
  ttsCostEur: number;
  /** Mindestens ein Preis fehlte — Summe ist dann eine Untergrenze */
  costIncomplete: boolean;
}

interface Options {
  /** Gespräch, in das geschrieben wird */
  sessionId: string | null;
  /** Nachricht an KIAN — der Aufrufer setzt den Sprachmodus-Hinweis */
  send: (text: string) => Promise<void>;
  /** Vom Server gespeicherte Sprach-Züge in den Chat übernehmen */
  onMessages: (messages: ChatMessage[]) => void;
  /** Letzte KIAN-Antwort im Gespräch */
  lastReply: VoiceReply | null;
  /** Chat sendet oder streamt gerade */
  busy: boolean;
}

const END_WORDS = /^(stopp|stop|ende|beenden|gespräch beenden|tschüss|danke das wars|das wars)$/;
const BARE_YES = /^(ja|jawohl|okay|ok|genau|passt|gerne|ja bitte|ja gerne|mach)$/;
const REPLY_TIMEOUT_MS = 180_000;
const EMPTY_USAGE: VoiceUsage = { heard: 0, sttCostEur: 0, spokenChars: 0, ttsCostEur: 0, costIncomplete: false };

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof DOMException && err.name === 'NotAllowedError') return 'Kein Zugriff aufs Mikrofon — bitte im Browser erlauben.';
  return err instanceof Error ? err.message : String(err);
}

export function useVoiceConversation({ sessionId, send, onMessages, lastReply, busy }: Options) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<VoicePhase>('off');
  const [level, setLevel] = useState(0);
  const [heardText, setHeardText] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<{ actionId: string; verb: 'anlegen' | 'ausführen' } | null>(null);
  const [usage, setUsage] = useState<VoiceUsage>(EMPTY_USAGE);

  const tokenRef = useRef(0);
  const ctxRef = useRef<AudioContext | null>(null);
  const playerRef = useRef<SpeechPlayer | null>(null);
  const captureRef = useRef<CaptureSession | null>(null);
  const pendingRef = useRef<typeof pending>(null);
  /** Knopf „Anlegen"/„Nein" — die Schleife führt ihn aus, damit nichts parallel spricht */
  const requestedRef = useRef<'confirm' | 'reject' | null>(null);
  const replyRef = useRef<{ reply: VoiceReply | null; busy: boolean }>({ reply: lastReply, busy });
  const waitersRef = useRef<Array<() => void>>([]);
  const sendRef = useRef(send);
  sendRef.current = send;
  const onMessagesRef = useRef(onMessages);
  onMessagesRef.current = onMessages;
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;

  // Jede Änderung an der letzten Antwort weckt die Wartenden
  useEffect(() => {
    replyRef.current = { reply: lastReply, busy };
    const waiters = waitersRef.current;
    waitersRef.current = [];
    waiters.forEach((w) => w());
  }, [lastReply, busy]);

  const setPendingDraft = (p: typeof pending) => {
    pendingRef.current = p;
    setPending(p);
  };

  const waitForReply = useCallback(async (beforeId: string | null, token: number): Promise<VoiceReply | null> => {
    const deadline = Date.now() + REPLY_TIMEOUT_MS;
    for (;;) {
      if (token !== tokenRef.current) return null;
      const { reply, busy: isBusy } = replyRef.current;
      if (reply && reply.id !== beforeId && !reply.streaming && !isBusy) return reply;
      if (Date.now() > deadline) throw new Error('KIAN antwortet nicht — bitte im Chat nachsehen.');
      await new Promise<void>((resolve) => {
        waitersRef.current.push(resolve);
        setTimeout(resolve, 1000);
      });
    }
  }, []);

  const speak = useCallback(async (text: string, token: number) => {
    if (token !== tokenRef.current || !text.trim()) return;
    setPhase('speaking');
    await playerRef.current?.speak(text);
  }, []);

  const stop = useCallback((message?: string) => {
    tokenRef.current++;
    requestedRef.current = null;
    captureRef.current?.cancel();
    captureRef.current = null;
    playerRef.current?.stop();
    setLevel(0);
    setPhase('off');
    setPendingDraft(null);
    if (message) setNotice(message);
  }, []);

  /** Sprach-Zug speichern lassen und in den Chat übernehmen */
  const turn = useCallback(
    async (kind: KianVoiceTurnKind, body: { actionId?: string | null; said?: string | null } = {}): Promise<KianVoiceTurnResult | null> => {
      const sid = sessionRef.current;
      if (!sid) return null;
      const res = await api.kianVoiceTurn({ sessionId: sid, kind, ...body });
      if (res.messages.length) onMessagesRef.current(res.messages);
      return res;
    },
    [],
  );

  const runDraftConfirm = useCallback(
    async (token: number, said?: string) => {
      const p = pendingRef.current;
      setPendingDraft(null);
      if (!p) return;
      setPhase('executing');
      let res: KianVoiceTurnResult | null;
      try {
        res = await turn('confirm', { actionId: p.actionId, said: said ?? null });
      } catch (err) {
        await speak(`Das ging nicht: ${errorText(err)}`, token);
        return;
      }
      void qc.invalidateQueries({ queryKey: kianActionKey(p.actionId) });
      if (!res?.executed) {
        await speak(res?.speak ?? '', token);
        return;
      }
      // Lauf abwarten — das Ergebnis speichert und formuliert der Server
      const until = Date.now() + 90_000;
      while (Date.now() < until && token === tokenRef.current) {
        await sleep(1500);
        const out = await turn('outcome', { actionId: p.actionId }).catch(() => null);
        if (out && !out.pending) {
          void qc.invalidateQueries({ queryKey: kianActionKey(p.actionId) });
          await speak(out.speak ?? '', token);
          return;
        }
      }
      if (token === tokenRef.current) await speak('Das dauert noch. Der Stand steht auf der Karte.', token);
    },
    [qc, speak, turn],
  );

  const runDraftReject = useCallback(
    async (token: number, said?: string) => {
      const p = pendingRef.current;
      setPendingDraft(null);
      if (!p) return;
      try {
        const res = await turn('reject', { actionId: p.actionId, said: said ?? null });
        void qc.invalidateQueries({ queryKey: kianActionKey(p.actionId) });
        await speak(res?.speak ?? '', token);
      } catch (err) {
        await speak(`Das ging nicht: ${errorText(err)}`, token);
      }
    },
    [qc, speak, turn],
  );

  const loop = useCallback(
    async (token: number) => {
      let silentRounds = 0;
      while (token === tokenRef.current) {
        // 1. Zuhören
        setPhase('listening');
        let session: CaptureSession;
        try {
          session = await startCapture(ctxRef.current!, { onLevel: setLevel, silenceMs: 1300, noSpeechMs: 15_000, maxMs: 60_000 });
        } catch (err) {
          stop(errorText(err));
          return;
        }
        if (token !== tokenRef.current) {
          session.cancel();
          return;
        }
        captureRef.current = session;
        const recorded = await session.done;
        captureRef.current = null;
        if (token !== tokenRef.current) return;
        const requested = requestedRef.current;
        requestedRef.current = null;
        if (requested && pendingRef.current) {
          await (requested === 'confirm' ? runDraftConfirm(token) : runDraftReject(token));
          continue;
        }
        if (!recorded.wav) {
          if (recorded.reason === 'no-speech' && ++silentRounds >= 3) {
            stop('Längere Zeit nichts gehört — Gespräch beendet.');
            return;
          }
          continue;
        }
        silentRounds = 0;

        // 2. Verstehen
        setPhase('transcribing');
        let text = '';
        try {
          const res = await transcribeKianVoice(recorded.wav);
          text = res.text.trim();
          setUsage((u) => ({
            ...u,
            heard: u.heard + 1,
            sttCostEur: u.sttCostEur + (res.costEur ?? 0),
            costIncomplete: u.costIncomplete || res.costEur == null,
          }));
        } catch (err) {
          stop(`Zuhören ging nicht: ${errorText(err)}`);
          return;
        }
        if (token !== tokenRef.current) return;
        setHeardText(text || null);
        if (!text) continue;
        const said = normalizeUtterance(text);

        // 3. Offener Entwurf? Nur „Ja, anlegen" führt aus
        if (pendingRef.current) {
          const intent = classifyConfirmation(text);
          if (intent === 'confirm') {
            await runDraftConfirm(token, text);
            continue;
          }
          if (intent === 'reject') {
            await runDraftReject(token, text);
            continue;
          }
          if (BARE_YES.test(said)) {
            const res = await turn('repeat-yes', { actionId: pendingRef.current.actionId, said: text }).catch(() => null);
            await speak(res?.speak ?? `Sag bitte „Ja, ${pendingRef.current.verb}".`, token);
            continue;
          }
          // Etwas anderes gesagt — der Entwurf bleibt auf der Karte, per Sprache nicht mehr bestätigbar
          setPendingDraft(null);
        }
        if (END_WORDS.test(said)) {
          const res = await turn('goodbye', { said: text }).catch(() => null);
          await speak(res?.speak ?? 'Bis später.', token);
          stop();
          return;
        }

        // 4. KIAN fragen
        setPhase('thinking');
        const beforeId = replyRef.current.reply?.id ?? null;
        let reply: VoiceReply | null = null;
        try {
          await sendRef.current(text);
          reply = await waitForReply(beforeId, token);
        } catch (err) {
          await speak(`Das ging schief: ${errorText(err)}`, token);
          continue;
        }
        if (!reply || token !== tokenRef.current) return;

        // 5. Vorlesen — die Antwort enthält ein angehängtes Vorlesen des Entwurfs schon
        await speak(toSpeakableText(reply.content), token);
        if (reply.voiceDraft?.confirmable && token === tokenRef.current) {
          setPendingDraft({ actionId: reply.voiceDraft.actionId, verb: reply.voiceDraft.verb });
        }
      }
    },
    [runDraftConfirm, runDraftReject, speak, stop, turn, waitForReply],
  );

  const start = useCallback(async () => {
    if (phase !== 'off') return;
    setNotice(null);
    setHeardText(null);
    setUsage(EMPTY_USAGE);
    if (!isCaptureSupported()) {
      setNotice('Dieser Browser kann nicht aufnehmen.');
      return;
    }
    const token = ++tokenRef.current;
    setPhase('starting');
    try {
      // Aus dem Klick heraus — sonst bleibt der Ton auf dem iPhone stumm
      if (!ctxRef.current || ctxRef.current.state === 'closed') ctxRef.current = createAudioContext();
      if (ctxRef.current.state === 'suspended') await ctxRef.current.resume();
      const config = await api.getKianVoiceConfig();
      if (!config.available) {
        stop(config.reason ?? 'Die Sprachfunktion ist nicht eingerichtet.');
        return;
      }
      playerRef.current = new SpeechPlayer(ctxRef.current, {
        server: true,
        onUsage: (u) =>
          setUsage((prev) => ({
            ...prev,
            spokenChars: prev.spokenChars + u.chars,
            ttsCostEur: prev.ttsCostEur + (u.costEur ?? 0),
            costIncomplete: prev.costIncomplete || u.costEur == null,
          })),
        onFallback: (reason) => setNotice(`Server-Stimme ausgefallen (${reason}) — der Browser liest vor.`),
      });
    } catch (err) {
      stop(errorText(err));
      return;
    }
    if (token !== tokenRef.current) return;
    void loop(token);
  }, [loop, phase, stop]);

  /** KIAN unterbrechen — danach wird wieder zugehört */
  const interrupt = useCallback(() => {
    playerRef.current?.stop();
  }, []);

  /** „Fertig gesprochen" — nicht auf die Pause warten */
  const finishTurn = useCallback(() => {
    captureRef.current?.stop();
  }, []);

  /** Entwurf per Knopf bestätigen/verwerfen (dasselbe wie „Ja, anlegen" / „Nein") */
  const confirmDraft = useCallback(() => {
    if (!pendingRef.current) return;
    requestedRef.current = 'confirm';
    captureRef.current?.cancel();
  }, []);

  const rejectDraft = useCallback(() => {
    if (!pendingRef.current) return;
    requestedRef.current = 'reject';
    captureRef.current?.cancel();
  }, []);

  // Anderes Gespräch geöffnet — die Züge gehörten sonst ins falsche
  useEffect(() => {
    stop();
  }, [sessionId, stop]);

  // Hintergrund oder Seitenwechsel beendet das Gespräch
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === 'hidden' && tokenRef.current > 0) stop();
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      stop();
      void ctxRef.current?.close().catch(() => {});
      ctxRef.current = null;
    };
  }, [stop]);

  return {
    phase,
    active: phase !== 'off',
    level,
    heardText,
    notice,
    pending,
    usage,
    start,
    stop: () => stop(),
    interrupt,
    finishTurn,
    confirmDraft,
    rejectDraft,
    clearNotice: () => setNotice(null),
  };
}
